import * as ordersModule from '@olxeu-a2b/ui/src/pages/orders';
import { useCallback, useEffect, useMemo } from 'react';
import { sdkFactory, useReducer } from '../../extras';
import useViewport from '../useViewport';
import { getTransactionForOrder, getAllTransactions, getTransactionForOrderV2 } from './api';
import {
  CLEAR_ACTIVE_TRANSACTION,
  CLEAR_TRANSACTIONS,
  GET_ACTIVE_TRANSACTION_ERROR,
  GET_ACTIVE_TRANSACTION_SUCCESS,
  GET_TRANSACTIONS_ERROR,
  GET_TRANSACTIONS_SUCCESS,
  LAST_TRANSACTIONS_REACHED,
  LOADING_ACTIVE_TRANSACTION,
  LOADING_TRANSACTIONS,
  NEXT_TRANSACTIONS_PAGE,
  PREVIOUS_TRANSACTIONS_PAGE,
  SET_CONTRACT_CANCELLED,
  SET_ORDER_REJECTED,
} from './transactionsActions';
import buildTransactionReducer, { firstState } from './transactionsReducer';
import {
  OrderConstants,
  TransactionsAction,
  TransactionsStore,
  TransactionType,
} from './useTransactions.d';

const { TAB_TYPE_BUYING, TAB_TYPE_SELLING } = ordersModule;
const { PAGE_SIZE_DESKTOP, PAGE_SIZE_MOBILE } = ordersModule as Partial<OrderConstants>;

const reducers: Record<TransactionType, React.Reducer<TransactionsStore, TransactionsAction>> = {
  [TAB_TYPE_BUYING]: buildTransactionReducer(TAB_TYPE_BUYING),
  [TAB_TYPE_SELLING]: buildTransactionReducer(TAB_TYPE_SELLING),
};
export default function useTransactions(type: TransactionType) {
  const { desktop } = useViewport();
  const reducer = reducers[type];

  const [state, dispatch] = useReducer(reducer, firstState);
  const sdk = useMemo(() => sdkFactory(), []);

  const pageSize = desktop ? PAGE_SIZE_DESKTOP : PAGE_SIZE_MOBILE;

  const getTransaction = useCallback((orderId: string) => {
    dispatch({ type: LOADING_ACTIVE_TRANSACTION });
    return getTransactionForOrder(sdk, orderId)
      .then(payload => dispatch({ type: GET_ACTIVE_TRANSACTION_SUCCESS, payload }))
      .catch(err => dispatch({ type: GET_ACTIVE_TRANSACTION_ERROR, payload: err }));
  }, []);

  const getTransactionV2 = useCallback((orderId: string) => {
    dispatch({ type: LOADING_ACTIVE_TRANSACTION });
    return getTransactionForOrderV2(sdk, orderId)
      .then(payload => dispatch({ type: GET_ACTIVE_TRANSACTION_SUCCESS, payload }))
      .catch(err => dispatch({ type: GET_ACTIVE_TRANSACTION_ERROR, payload: err }));
  }, []);

  const getTransactions = useCallback(() => {
    dispatch({ type: LOADING_TRANSACTIONS(type) });

    return getAllTransactions(sdk, type, state.page, pageSize)
      .then(parsed => {
        if (parsed.length < pageSize) {
          dispatch({ type: LAST_TRANSACTIONS_REACHED(type) });
        }

        dispatch({ type: GET_TRANSACTIONS_SUCCESS(type), payload: parsed });
      })
      .catch(err => dispatch({ type: GET_TRANSACTIONS_ERROR(type), payload: err }));
  }, [type, state.page]);

  const nextPage = useCallback(() => {
    dispatch({ type: NEXT_TRANSACTIONS_PAGE(type) });
  }, [type]);

  const prevPage = useCallback(() => dispatch({ type: PREVIOUS_TRANSACTIONS_PAGE(type) }), [type]);

  const setOrderRejected = useCallback(
    ({ orderId, active = false }: { orderId: string; active?: boolean }) =>
      dispatch({
        type: SET_ORDER_REJECTED(type),
        payload: { orderId, active },
      }),
    [type],
  );

  const setContractCancelled = useCallback(
    ({ orderId, active = false }: { orderId: string; active?: boolean }) =>
      dispatch({
        type: SET_CONTRACT_CANCELLED(type),
        payload: { orderId, active },
      }),
    [type],
  );

  const clearTransactions = useCallback(() => dispatch({ type: CLEAR_TRANSACTIONS(type) }), [type]);

  const clearActiveTransaction = useCallback(
    () => dispatch({ type: CLEAR_ACTIVE_TRANSACTION }),
    [],
  );

  useEffect(() => {
    if (state.page !== 0) {
      getTransactions();
    }
  }, [state.page]);

  return {
    ...state,
    getTransaction,
    getTransactionV2,
    getTransactions,
    nextPage,
    prevPage,
    setOrderRejected,
    setContractCancelled,
    clearTransactions,
    clearActiveTransaction,
  };
}
